import { calculateCardValue, getSpecialEffect, getNextPlayerIndex } from '../utils/gameLogic';

// Easy: pick a random valid card (one that doesn't bust if possible)
export function easyAI(hand, currentSum, players, playerIndex, direction) {
  const valid = hand.filter(card => {
    const v = calculateCardValue(card, currentSum);
    return Math.max(0, currentSum + v) <= 99;
  });
  if (valid.length > 0) {
    return valid[Math.floor(Math.random() * valid.length)];
  }
  // All bust — pick the one with lowest resulting sum
  return hand.reduce((best, card) => {
    const v = calculateCardValue(card, currentSum);
    const bv = calculateCardValue(best, currentSum);
    return (currentSum + v) < (currentSum + bv) ? card : best;
  });
}

// Normal: current behavior — tries to target human, uses specials smartly
export function normalAI(hand, currentSum, players, playerIndex, direction) {
  let bestCard = null;
  let bestValue = Infinity;

  const nextIdx = getNextPlayerIndex(playerIndex, players, direction, false);
  const isHumanNext = nextIdx === 0;

  // Try to push sum high if human is next
  if (isHumanNext && currentSum < 85) {
    for (const card of hand) {
      const v = calculateCardValue(card, currentSum);
      const newSum = Math.max(0, currentSum + v);
      if (newSum >= 85 && newSum <= 95) {
        return card;
      }
    }
  }

  // Try specials
  for (const card of hand) {
    const v = calculateCardValue(card, currentSum);
    const newSum = Math.max(0, currentSum + v);
    const se = getSpecialEffect(card);
    if (se === 'skip' && isHumanNext && newSum <= 99) return card;
    if (se === 'reverse' && newSum <= 99) return card;
  }

  // Play lowest safe card
  for (const card of hand) {
    const v = calculateCardValue(card, currentSum);
    const newSum = Math.max(0, currentSum + v);
    if (newSum <= 99 && newSum < bestValue) { bestValue = newSum; bestCard = card; }
  }

  if (!bestCard) {
    // All bust — pick least bad
    for (const card of hand) {
      const v = calculateCardValue(card, currentSum);
      const newSum = Math.max(0, currentSum + v);
      if (newSum < bestValue) { bestValue = newSum; bestCard = card; }
    }
  }

  return bestCard;
}

// Hard: strategic — hoards specials, sets traps, plays aggressively
export function hardAI(hand, currentSum, players, playerIndex, direction) {
  const nextIdx = getNextPlayerIndex(playerIndex, players, direction, false);
  const isHumanNext = nextIdx === 0;

  const specials = hand.filter(c => ['4', '9', '10', 'K', 'A'].includes(c.value));
  const normals = hand.filter(c => !['4', '9', '10', 'K', 'A'].includes(c.value));

  // If sum is high (>85) and human is next, push it higher with a normal card
  if (isHumanNext && currentSum >= 80) {
    // Play highest normal card that stays under 99
    const sorted = [...normals].sort((a, b) => {
      return calculateCardValue(b, currentSum) - calculateCardValue(a, currentSum);
    });
    for (const card of sorted) {
      const newSum = Math.max(0, currentSum + calculateCardValue(card, currentSum));
      if (newSum <= 99 && newSum >= currentSum) return card;
    }
    // Use K to set to 99 if human is next
    const king = hand.find(c => c.value === 'K');
    if (king) return king;
  }

  // If sum is low and human is NOT next, hoard specials — play cheapest normal
  if (currentSum < 70 && normals.length > 0) {
    const sorted = [...normals].sort((a, b) => {
      return calculateCardValue(a, currentSum) - calculateCardValue(b, currentSum);
    });
    for (const card of sorted) {
      const newSum = Math.max(0, currentSum + calculateCardValue(card, currentSum));
      if (newSum <= 99) return card;
    }
  }

  // Skip human if possible
  if (isHumanNext) {
    const skip = hand.find(c => c.value === '4');
    if (skip) return skip;
  }

  // Use reverse strategically
  if (currentSum >= 85 && !isHumanNext) {
    const reverse = hand.find(c => c.value === '9');
    if (reverse) return reverse;
  }

  // Use -10 if sum is getting high
  if (currentSum >= 80) {
    const minus = hand.find(c => c.value === '10');
    if (minus) return minus;
  }

  // Fallback to normal AI
  return normalAI(hand, currentSum, players, playerIndex, direction);
}

export function selectCard(difficulty, hand, currentSum, players, playerIndex, direction) {
  switch (difficulty) {
    case 'easy': return easyAI(hand, currentSum, players, playerIndex, direction);
    case 'hard': return hardAI(hand, currentSum, players, playerIndex, direction);
    default: return normalAI(hand, currentSum, players, playerIndex, direction);
  }
}
